import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Button, Typography, colors, Spin } from 'syngenta-digital-cropwise-react-ui-kit';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useBreakpoint } from 'hooks';

const { Title, Text } = Typography;

export interface SectionItem {
  id: string;
  name: string;
  displayName: string;
}

export interface SectionReorderModalProps {
  isVisible: boolean;
  onClose: () => void;
  onSubmit: (newOrder: string[]) => void;
  currentOrder: string[];
}

const StyledModal = styled(Modal)`
  .syt-antd-modal-content {
    border-radius: 8px;
  }
  
  @media (max-width: 768px) {
    .syt-antd-modal-content {
      margin: 20px;
      max-width: calc(100vw - 40px);
    }
  }
`;

const StyledDragDropContainer = styled.div`
  margin: 20px 0;
  padding: 16px;
  border: 1px solid ${colors.neutral30};
  border-radius: 8px;
  background-color: ${colors.neutral10};
  max-height: 60vh;
  overflow-y: auto;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${colors.neutral20};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${colors.neutral50};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${colors.neutral60};
  }
`;

const StyledDragItem = styled.div<{ isDragging: boolean; isDragOver: boolean }>`
  background: ${(props) => {
    if (props.isDragging) {
      return colors.blue20;
    }
    if (props.isDragOver) {
      return colors.blue10;
    }
    return colors.neutral00;
  }};
  border: 1px solid ${(props) => {
    if (props.isDragging || props.isDragOver) {
      return colors.blue40;
    }
    return colors.neutral30;
  }};
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 8px;
  cursor: move;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: ${(props) => (props.isDragging ? '0 4px 8px rgba(0, 0, 0, 0.15)' : 'none')};
  transition: all 0.2s ease;
  user-select: none;
  opacity: ${(props) => (props.isDragging ? 0.7 : 1)};

  &:hover {
    background: ${colors.neutral20};
    border-color: ${colors.blue40};
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const StyledDragHandle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  cursor: move;
  padding: 4px;
  
  span {
    width: 4px;
    height: 4px;
    background-color: ${colors.neutral60};
    border-radius: 50%;
    display: block;
  }
  
  @media (max-width: 768px) {
    padding: 8px;
    
    span {
      width: 6px;
      height: 6px;
    }
  }
`;

const StyledButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`;

const StyledMoveButton = styled(Button)`
  padding: 4px 8px;
  height: auto;
  font-size: 12px;
  min-width: auto;
`;

const StyledModalFooter = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
    
    & > button:first-child {
      order: 3;
      margin-right: 0;
    }
  }
`;

const StyledInstructions = styled(Text)`
  color: ${colors.neutral70};
  margin-bottom: 16px;
  font-size: 14px;
`;

const SECTION_ITEMS: SectionItem[] = [
  { id: 'recommendation-info', name: 'recommendation info section', displayName: 'Recommendation Information' },
  { id: 'soil-characteristics', name: 'soil characteristics map section', displayName: 'Map and Products' },
  { id: 'expert-seed-advice', name: 'expert seed advice section', displayName: 'Recommended Products' },
  { id: 'product-comparison', name: 'product comparison section', displayName: 'Product Comparison' },
  { id: 'product-performance', name: 'product performance section', displayName: 'Product Performance by Stress Level' },
  { id: 'agronomic-preferences', name: 'agronomic preferences section', displayName: 'Agronomic Preferences' },
  { id: 'environmental-stress', name: 'environmental stress section', displayName: 'Environmental Stress' },
  { id: 'trial-information', name: 'trial information section', displayName: 'Trialing Information' },
  { id: 'growth-stages', name: 'management and planting advice section', displayName: 'Management and Planting Advice' },
  { id: 'herbicide-technology', name: 'herbicide technology and weed control section', displayName: 'Herbicide Technology & Weed Control' },
  { id: 'seed-density', name: 'seed density advices section', displayName: 'Seed Density Demand and Advice' },
];

const SectionReorderModal: React.FC<SectionReorderModalProps> = ({
  isVisible,
  onClose,
  onSubmit,
  currentOrder,
}) => {
  const [t] = useTranslation();
  const { isMobile } = useBreakpoint();
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [isApplying, setIsApplying] = useState(false);

  // Helper function to create ordered sections based on current order
  const createOrderedSections = useCallback((order: string[]) => {
    return order.map(id => SECTION_ITEMS.find(item => item.id === id)).filter(Boolean) as SectionItem[];
  }, []);

  const [sections, setSections] = useState<SectionItem[]>(() => createOrderedSections(currentOrder));

  // Update sections when modal opens or currentOrder changes
  useEffect(() => {
    if (isVisible) {
      setSections(createOrderedSections(currentOrder));
    }
  }, [isVisible, currentOrder, createOrderedSections]);

  const moveSection = useCallback((fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= sections.length) {
      return;
    }

    const newSections = [...sections];
    const [movedItem] = newSections.splice(fromIndex, 1);
    newSections.splice(toIndex, 0, movedItem);
    setSections(newSections);
  }, [sections]);

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget.outerHTML);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== dropIndex) {
      moveSection(draggedIndex, dropIndex);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };
  const handleSubmit = async () => {
    setIsApplying(true);
    try {
      const newOrder = sections.map(section => section.id);
      // Add a small delay to show the loading state
      await new Promise(resolve => setTimeout(resolve, 500));
      onSubmit(newOrder);
      onClose();
    } finally {
      setIsApplying(false);
    }
  }; const handleCancel = () => {
    if (isApplying) {
      return;
    } // Prevent closing while applying changes
    setSections(createOrderedSections(currentOrder)); // Reset to current order
    onClose();
  }; const handleReset = () => {
    if (isApplying) {
      return;
    } // Prevent reset while applying changes

    // Use the default order defined in SECTION_ITEMS
    const defaultOrderIds = SECTION_ITEMS.map(item => item.id);
    setSections(createOrderedSections(defaultOrderIds));
  };
  return ( <StyledModal
    title={
      <Title level={4} style={{ margin: 0 }}>
        {t('Reorder Report Sections')}
      </Title>
    }
    open={isVisible}
    onCancel={handleCancel}
    footer={null}
    width={isMobile ? '90%' : 600}
    centered
    maskClosable={!isApplying}
    closable={!isApplying}
  ><StyledInstructions>
      {t('Drag and drop sections to reorder them, or use the up and down arrows. The header and footer will remain in their current positions.')}
    </StyledInstructions>      <StyledDragDropContainer>
      <Spin spinning={isApplying} tip={t('Applying changes...')}>
        {sections.map((section, index) => (
          <StyledDragItem
            key={section.id}
            isDragging={draggedIndex === index}
            isDragOver={dragOverIndex === index}
            draggable={!isApplying}
            onDragStart={(e) => !isApplying && handleDragStart(e, index)}
            onDragOver={(e) => !isApplying && handleDragOver(e, index)}
            onDragLeave={!isApplying ? handleDragLeave : undefined}
            onDrop={(e) => !isApplying && handleDrop(e, index)}
            onDragEnd={!isApplying ? handleDragEnd : undefined}
          >
            <StyledDragHandle>
              <span />
              <span />
              <span />
            </StyledDragHandle>
            <Text strong style={{ flex: 1 }}>{section.displayName}</Text>
            <StyledButtonContainer>
              <StyledMoveButton
                size="small"
                disabled={isApplying || index === 0}
                onClick={() => !isApplying && moveSection(index, index - 1)}
                title={t('Move up')}
              >
                  ↑
              </StyledMoveButton>
              <StyledMoveButton
                size="small"
                disabled={isApplying || index === sections.length - 1}
                onClick={() => !isApplying && moveSection(index, index + 1)}
                title={t('Move down')}
              >
                  ↓
              </StyledMoveButton>
            </StyledButtonContainer>
          </StyledDragItem>
        ))}
      </Spin>
    </StyledDragDropContainer>      <StyledModalFooter>
      <Button onClick={handleReset} style={{ marginRight: 'auto' }} disabled={isApplying}>
        {t('Reset to Default')}
      </Button>
      <Button onClick={handleCancel} disabled={isApplying}>
        {t('Cancel')}
      </Button>
      <Button type="primary" onClick={handleSubmit} loading={isApplying} disabled={isApplying}>
        {t('Apply Changes')}
      </Button>
    </StyledModalFooter>
  </StyledModal>
  );
};

export default SectionReorderModal;
